import React, { useEffect, useRef } from 'react';

interface VehicleMapProps {
  activeTab: 'my' | 'all';
}

declare global {
  interface Window {
    AMap: any;
  }
}

const VehicleMap: React.FC<VehicleMapProps> = ({ activeTab }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);

  useEffect(() => {
    // 加载高德地图API
    const script = document.createElement('script');
    script.src = `https://webapi.amap.com/maps?v=1.4.15&key=YOUR_AMAP_KEY`;
    script.async = true;
    
    script.onload = () => {
      if (mapRef.current && window.AMap) {
        initMap();
      }
    };

    document.head.appendChild(script);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.destroy();
      }
    };
  }, []);

  const initMap = () => {
    if (!mapRef.current || !window.AMap) return;

    // 初始化地图
    mapInstance.current = new window.AMap.Map(mapRef.current, {
      zoom: 13,
      center: [114.069336, 22.544544], // 深圳坐标
      mapStyle: 'amap://styles/normal'
    });

    // 添加车辆标记
    addVehicleMarkers();
  };

  const addVehicleMarkers = () => {
    if (!mapInstance.current) return;

    // 清除之前的标记
    if (mapInstance.current.getOverlays) {
      mapInstance.current.getOverlays().forEach((overlay: any) => {
        if (overlay instanceof window.AMap.Marker) {
          mapInstance.current.remove(overlay);
        }
      });
    }

    // 模拟车辆数据
    let vehicles = [];
    
    if (activeTab === 'my') {
      // 我的车辆：只显示一辆车，居中显示
      vehicles = [
        { 
          position: [114.069336, 22.544544], 
          name: '岚图梦想家', 
          available: true,
          description: '我的车辆'
        }
      ];
      
      // 调整地图中心到我的车辆位置
      mapInstance.current.setCenter([114.069336, 22.544544]);
      mapInstance.current.setZoom(15);
    } else {
      // 全部车辆：显示多辆车
      vehicles = [
        { 
          position: [114.069336, 22.544544], 
          name: '岚图梦想家', 
          available: true,
          description: '车辆1'
        },
        { 
          position: [114.079336, 22.554544], 
          name: '岚图梦想家', 
          available: false,
          description: '车辆2'
        },
        { 
          position: [114.059336, 22.534544], 
          name: '岚图梦想家', 
          available: true,
          description: '车辆3'
        },
        { 
          position: [114.089336, 22.564544], 
          name: '岚图梦想家', 
          available: true,
          description: '车辆4'
        }
      ];
      
      // 调整地图视野以显示所有车辆，并设置较小的缩放级别
      mapInstance.current.setFitView();
      // 将缩放级别调小4级，显示更大范围
      const currentZoom = mapInstance.current.getZoom();
      mapInstance.current.setZoom(Math.max(12, 8)); // 最小缩放级别为8
    }

    vehicles.forEach(vehicle => {
      // 使用 Lucide React 的 Car 图标的真实 SVG 数据
      // 这是从 node_modules/lucide-react/dist/esm/icons/car.js 直接读取的
      const carIconSvg = `
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="12" fill="${vehicle.available ? '#D1FAE5' : '#F3F4F6'}"/>
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" stroke="${vehicle.available ? '#059669' : '#6B7280'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <circle cx="7" cy="17" r="2" fill="${vehicle.available ? '#059669' : '#6B7280'}"/>
          <path d="M9 17h6" stroke="${vehicle.available ? '#059669' : '#6B7280'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          <circle cx="17" cy="17" r="2" fill="${vehicle.available ? '#059669' : '#6B7280'}"/>
        </svg>
      `;

      const marker = new window.AMap.Marker({
        position: vehicle.position,
        title: vehicle.name,
        icon: new window.AMap.Icon({
          size: new window.AMap.Size(32, 32),
          image: 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(carIconSvg)))
        })
      });

      // 添加车辆信息窗口
      const infoWindow = new window.AMap.InfoWindow({
        content: `
          <div style="padding: 10px; min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #333;">${vehicle.name}</h4>
            <p style="margin: 0; color: #666;">描述: ${vehicle.description}</p>
            <p style="margin: 0; color: #666;">状态: ${vehicle.available ? '可用' : '不可用'}</p>
            <p style="margin: 0; color: #666;">位置: 深圳市南山区</p>
          </div>
        `,
        offset: new window.AMap.Pixel(0, -30)
      });

      // 点击标记显示信息窗口
      marker.on('click', () => {
        infoWindow.open(mapInstance.current, vehicle.position);
      });

      marker.setMap(mapInstance.current);
    });
  };

  useEffect(() => {
    if (mapInstance.current) {
      addVehicleMarkers();
    }
  }, [activeTab]);

  return (
    <div className="w-full h-64 bg-gray-200">
      <div 
        ref={mapRef} 
        className="w-full h-full"
        style={{ minHeight: '256px' }}
      />
    </div>
  );
};

export default VehicleMap;
